/**
 * Checkout Store — hosted-checkout redirect helper.
 *
 * After the pivot to Stripe Hosted Checkout, there is no in-app checkout
 * modal. This store now exposes a single `openCheckout()` action that calls
 * `/billing/create-checkout-session` (authenticated → upgrade branch) and
 * redirects the browser to the Stripe-hosted URL.
 *
 * Kept as a Zustand store (rather than a plain function) so existing call
 * sites — CommandPalette, UploadPage, PaywallLock, SettingsPage,
 * SettingsBillingTab, DashboardUploadOverlay, BillingSettings,
 * SubscriptionBadge — don't need to change.
 *
 * Checkout opens in a NEW TAB: we synchronously open a splash route
 * (`/checkout/redirecting`, the brand CockpitLoader) on click — before any await,
 * so the popup blocker treats it as user-initiated — then point that tab at the
 * Stripe URL once the session is created. If the tab can't be opened (popup blocked, or
 * openCheckout was called outside a click gesture, e.g. an upload-403 handler),
 * we fall back to a same-tab redirect. Opening the tab also writes the
 * cross-tab "upgrade pending" flag so the original tab auto-refreshes to Pro
 * after payment (see `hooks/useTierUpgradeSync.ts`).
 */

import { detectLocale } from '../lib/detectLocale';
import { create } from 'zustand';
import { api, apiErrorMessage } from '../api/client';
import { safeExternalRedirect } from '../lib/safeRedirect';
import { markUpgradePending } from '../lib/tierSync';
import { useUIStore } from './uiStore';

const SPLASH_URL = '/checkout/redirecting';

type BillingInterval = 'monthly' | 'annual';

interface CheckoutStore {
  redirecting: boolean;
  error: string | null;
  openCheckout: (interval?: BillingInterval) => Promise<void>;
}

// detectLocale lifted to src/lib/detectLocale.ts (Phase 2 — was triplicated).

export const useCheckoutStore = create<CheckoutStore>((set, get) => ({
  redirecting: false,
  error: null,

  openCheckout: async (interval: BillingInterval = 'monthly') => {
    if (get().redirecting) {return;}

    // Open the checkout tab + splash synchronously (must precede any await so the
    // browser counts it as user-initiated). `null` when popups are blocked or when
    // called outside a click gesture — we fall back to a same-tab redirect below.
    const tab = typeof window !== 'undefined' ? window.open(SPLASH_URL, '_blank') : null;
    markUpgradePending();
    set({ redirecting: true, error: null });

    const fail = (message: string) => {
      if (tab && !tab.closed) {tab.close();}
      set({ redirecting: false, error: message });
      useUIStore.getState().showToast(message, 'error');
    };

    try {
      const tier = interval === 'annual' ? 'pro_annual' : 'pro_monthly';
      const { checkoutUrl } = await api.billing.createCheckoutSession(tier, detectLocale());

      if (tab && !tab.closed) {
        // Checkout opens in the new tab. Point it at Stripe, then release THIS
        // tab — it stays on the dashboard and isn't navigating, so it must clear
        // its own loading state (otherwise the button is stuck on "Redirecting").
        if (safeExternalRedirect(checkoutUrl, tab)) {
          set({ redirecting: false });
        } else {
          fail('Could not start checkout. Try again.');
        }
      } else {
        // Popup blocked / non-gesture path → this tab redirects itself (and
        // navigates away, so its loading state is moot).
        if (!safeExternalRedirect(checkoutUrl)) {
          fail('Could not start checkout. Try again.');
        }
      }
    } catch (e) {
      fail(apiErrorMessage(e, 'Could not start checkout. Try again.'));
    }
  },
}));
